import { useCallback, useEffect, useRef, useState } from "react";
import { sendToolResult } from "@/features/intelligence/api/client";
import {
  accessWhenKnown,
  NOTHING_ARMED,
  warmAccessProbe,
  type AccessAnswer,
} from "@/features/intelligence/lib/access-probe";
import {
  HANDLER_BUDGET_MS,
  type FrontendCallRecord,
} from "@/features/intelligence/lib/frontend-tool-outcome";
import type { FrontendToolRunner } from "@/features/intelligence/lib/frontend-tool-run";
import { toolActivityFor } from "./runtime";
import type { FrontendToolsBinding, MutableRef } from "./types";
import type { ThreadState } from "@/features/intelligence/lib/thread-state";

export function useFrontendToolController({
  threadRef,
  context,
  binding,
}: {
  threadRef: MutableRef<ThreadState>;
  context: object | null | undefined;
  binding: FrontendToolsBinding | null | undefined;
}) {
  const [frontendCalls, setFrontendCalls] = useState<ReadonlyMap<string, FrontendCallRecord>>(
    () => new Map(),
  );
  const contextRef = useRef(context);
  contextRef.current = context;
  const bindingRef = useRef(binding);
  bindingRef.current = binding;
  const runnerRef = useRef<FrontendToolRunner | null>(null);
  const toolsRef = useRef<FrontendTools | null>(null);

  useEffect(() => {
    warmAccessProbe();
    void loadFrontendTools();
  }, []);

  const reset = useCallback(() => {
    runnerRef.current = null;
    setFrontendCalls(new Map());
  }, []);

  const resolveAccess = useCallback(async (): Promise<AccessAnswer> => {
    if (!bindingRef.current) return NOTHING_ARMED;
    const [access, tools] = await Promise.all([
      accessWhenKnown(),
      withinArmingWait(loadFrontendTools()),
    ]);
    toolsRef.current = tools;
    return access;
  }, []);

  const arm = useCallback((access: AccessAnswer): object[] => {
    const current = bindingRef.current;
    const tools = toolsRef.current;
    const declared =
      access.access === "ok" && current && tools
        ? tools.declareFrontendTools(current.capabilities)
        : [];
    runnerRef.current =
      declared.length > 0 && current && tools
        ? tools.createFrontendToolRunner({ host: current.host, armed: tools.armedNames(declared) })
        : null;
    setFrontendCalls(new Map());
    return declared;
  }, []);

  const run = useCallback(
    async (conversationId: string, runId: string, toolCallId: string) => {
      const runner = runnerRef.current;
      if (!runner) return;
      const activity = toolActivityFor(threadRef.current, toolCallId);
      if (!activity) return;

      const outcome = await runner.dispatch(toolCallId, activity.toolName, activity.argsText);
      if (outcome.kind !== "ran") return;

      setFrontendCalls(new Map(runner.records()));
      const report = await sendToolResult(conversationId, runId, {
        toolCallId,
        ...outcome.report,
      });
      if (!report.ok) {
        console.warn(
          `[simon] frontend tool result not delivered: status=${report.error.status} ` +
            `code=${report.error.code ?? "(none)"}`,
        );
      }
      bindingRef.current?.host.reported?.(activity.toolName, toolCallId);
    },
    [threadRef],
  );

  return {
    frontendCalls,
    contextRef,
    runnerRef,
    resolveAccess,
    arm,
    reset,
    run,
  };
}

function loadFrontendTools(): Promise<FrontendTools | null> {
  loading ??= Promise.all([
    import("@/features/intelligence/lib/frontend-tools"),
    import("@/features/intelligence/lib/frontend-tool-run"),
  ]).then(
    ([declarations, runner]) => ({
      declareFrontendTools: declarations.declareFrontendTools,
      armedNames: declarations.armedNames,
      createFrontendToolRunner: runner.createFrontendToolRunner,
    }),
    (error: unknown) => {
      loading = null;
      console.warn(
        "[simon] the frontend tools could not be loaded, so this turn declares none:",
        error instanceof Error ? error.message : "unknown",
      );
      return null;
    },
  );
  return loading;
}

function withinArmingWait(tools: Promise<FrontendTools | null>): Promise<FrontendTools | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const waited = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      console.warn("[simon] the frontend tools are still loading, so this turn declares none");
      resolve(null);
    }, TOOLS_WAIT_MS);
  });
  return Promise.race([tools, waited]).finally(() => clearTimeout(timer));
}

interface FrontendTools {
  declareFrontendTools: typeof import("@/features/intelligence/lib/frontend-tools").declareFrontendTools;
  armedNames: typeof import("@/features/intelligence/lib/frontend-tools").armedNames;
  createFrontendToolRunner: typeof import("@/features/intelligence/lib/frontend-tool-run").createFrontendToolRunner;
}

let loading: Promise<FrontendTools | null> | null = null;

const TOOLS_WAIT_MS = HANDLER_BUDGET_MS / 2;
